// Cookie utility functions for authentication

// Simple cache to reduce repeated cookie parsing
let cookieCache = {};
let cacheTimestamp = 0;
const CACHE_DURATION = 1000; // 1 second cache duration

// Set a cookie with expiration
export const setCookie = (name, value, days = 1) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  
  const cookieString = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Strict;Secure=${window.location.protocol === 'https:'}`;
  document.cookie = cookieString;
};

// Get a cookie value
export const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
  }
  
  return null;
};

// Remove a cookie
export const removeCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;SameSite=Strict`;
  
  // Clear from cache
  if (cookieCache[name] !== undefined) {
    cookieCache[name] = null;
    cacheTimestamp = Date.now();
  }
};

// Check if a cookie exists and is valid
export const isCookieValid = (name) => {
  const cookieValue = getCookie(name);
  if (!cookieValue) return false;
  
  try {
    const parsed = JSON.parse(cookieValue);
    if (parsed.expiry && new Date().getTime() < parsed.expiry) {
      return true;
    }
    // Cookie is expired, remove it
    removeCookie(name);
    return false;
  } catch (error) {
    console.error('Error parsing cookie:', error);
    removeCookie(name);
    return false;
  }
};

// Get parsed cookie data with caching
export const getCookieData = (name) => {
  const now = Date.now();
  
  // Check cache first
  if (cookieCache[name] && (now - cacheTimestamp) < CACHE_DURATION) {
    return cookieCache[name];
  }
  
  // Clear cache if expired
  if ((now - cacheTimestamp) >= CACHE_DURATION) {
    cookieCache = {};
    cacheTimestamp = now;
  }
  
  const cookieValue = getCookie(name);
  console.log(`[${name}] Cookie value:`, cookieValue);
  if (!cookieValue) {
    cookieCache[name] = null;
    return null;
  }
  
  try {
    const parsed = JSON.parse(cookieValue);
    
    if (parsed.expiry && new Date().getTime() < parsed.expiry) {
      cookieCache[name] = parsed;
      return parsed;
    } else {
      removeCookie(name);
      cookieCache[name] = null;
      return null;
    }
  } catch (error) {
    console.error('Error parsing cookie data:', error);
    removeCookie(name);
    cookieCache[name] = null;
    return null;
  }
};

// Set authentication data in cookie
export const setAuthCookie = (name, token, userData, days = 1) => {
  const expiryTime = new Date().getTime() + (days * 24 * 60 * 60 * 1000);
  const authData = {
    token,
    expiry: expiryTime,
    user: userData
  };
  setCookie(name, JSON.stringify(authData), days);
  
  // Update cache
  cookieCache[name] = authData;
  cacheTimestamp = Date.now();
};

// Clear all authentication cookies
export const clearAllAuthCookies = () => {
  const authCookies = ['auth_token', 'auth_tokena'];
  authCookies.forEach(cookieName => {
    removeCookie(cookieName);
    // Clear from cache
    cookieCache[cookieName] = null;
  });
  cacheTimestamp = Date.now();
};

// Get all valid authentication cookies
export const getAllAuthCookies = () => {
  const authCookies = ['auth_token', 'auth_tokena'];
  const validCookies = {};
  
  authCookies.forEach(cookieName => {
    const data = getCookieData(cookieName);
    if (data) {
      validCookies[cookieName] = data;
    }
  });
  
  return validCookies;
};

// Check if any authentication cookie is valid
export const hasValidAuthCookie = () => {
  const authCookies = ['auth_token', 'auth_tokena'];
  return authCookies.some(cookieName => isCookieValid(cookieName));
};

// Get user role from cookies
export const getUserRoleFromCookies = () => {
  if (isCookieValid('auth_tokena')) return 'أدمن';
  if (isCookieValid('auth_token')) return 'طالب';
  return null;
};

// Get current user's token (optimized to avoid unnecessary null lookups)
export const getCurrentUserToken = () => {
  // Try student token first (more common)
  const studentData = getCookieData('auth_token');
  if (studentData?.token) {
    return studentData.token;
  }
  
  // Only check admin token if student token is not available
  const adminData = getCookieData('auth_tokena');
  return adminData?.token || null;
};

// Get current user's full cookie data
export const getCurrentUserData = () => {
  // Try student token first
  const studentData = getCookieData('auth_token');
  if (studentData) {
    return studentData;
  }
  
  // Only check admin token if student token is not available
  const adminData = getCookieData('auth_tokena');
  return adminData || null;
};

// Get user data from cookies
export const getUserDataFromCookies = () => {
  const role = getUserRoleFromCookies();
  if (!role) return null;

  let cookieName;
  switch (role) {
    case 'أدمن':
      cookieName = 'auth_tokena';
      break;
    case 'طالب':
      cookieName = 'auth_token';
      break;
    default:
      return null;
  }

  const cookieData = getCookieData(cookieName);
  if (cookieData) {
    return {
      ...cookieData.user,
      role,
      token: cookieData.token
    };
  }
  
  return null;
};
